import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { MessageSquare, CheckCircle, Clock, User, Calendar } from 'lucide-react';
import { getUserFeedback, markFeedbackAsRead, FeedbackSubmission } from '@/lib/dailyUsage';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface UserFeedbackInboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUnreadCountChange?: (count: number) => void;
}

const TAG_CONFIG = {
  report: { label: 'Report', color: 'text-destructive' },
  issue: { label: 'Issue', color: 'text-amber-500' },
  doubt: { label: 'Doubt', color: 'text-blue-500' },
  suggestion: { label: 'Suggestion', color: 'text-emerald-500' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'text-blue-500' },
  in_progress: { label: 'In Progress', color: 'text-amber-500' },
  resolved: { label: 'Resolved', color: 'text-emerald-500' },
};

export function UserFeedbackInbox({ open, onOpenChange, onUnreadCountChange }: UserFeedbackInboxProps) {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<FeedbackSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);

  useEffect(() => {
    if (open && user) {
      setHasMarkedAsRead(false);
      fetchFeedback();
    }
  }, [open, user]);

  useEffect(() => {
    // Update parent with current unread count
    const unreadCount = feedback.filter(f => f.hasUnreadReply).length;
    onUnreadCountChange?.(unreadCount);
  }, [feedback, onUnreadCountChange]);

  const fetchFeedback = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getUserFeedback(user.uid);
      setFeedback(data);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (isOpen: boolean) => {
    if (!isOpen && !hasMarkedAsRead && user) {
      // Mark all as read when closing the dialog
      const unreadItems = feedback.filter(f => f.hasUnreadReply);
      if (unreadItems.length > 0) {
        for (const item of unreadItems) {
          if (item.id) {
            await markFeedbackAsRead(item.id);
          }
        }
        setHasMarkedAsRead(true);
        // Refresh to clear notification flags
        await fetchFeedback();
      }
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            My Feedback
          </DialogTitle>
          <DialogDescription>
            View your submitted feedback and admin responses
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No feedback submitted yet</p>
            </div>
          ) : (
            feedback.map((item) => (
              <Card key={item.id} className={cn(
                "p-4",
                item.hasUnreadReply && "ring-2 ring-primary"
              )}>
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={TAG_CONFIG[item.tag].color}>
                      {TAG_CONFIG[item.tag].label}
                    </Badge>
                    <Badge variant="outline" className={STATUS_CONFIG[item.status].color}>
                      {STATUS_CONFIG[item.status].label}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {format(new Date(item.createdAt), 'MMM d, h:mm a')}
                  </span>
                </div>

                {/* Original Message */}
                <div className="mb-3">
                  <p className="text-sm font-medium mb-1">Your Message:</p>
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">
                    {item.message}
                  </p>
                </div>

                {/* Replies */}
                {item.replies && item.replies.length > 0 && (
                  <>
                    <Separator className="my-3" />
                    <div className="space-y-3">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Admin Replies:
                      </p>
                      {item.replies.map((reply) => (
                        <div
                          key={reply.id}
                          className="bg-primary/5 border border-primary/20 rounded p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-primary">
                              {reply.repliedByName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(reply.repliedAt), 'MMM d, h:mm a')}
                            </span>
                          </div>
                          <p className="text-sm">{reply.message}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Resolved Info */}
                {item.status === 'resolved' && item.resolvedByName && (
                  <>
                    <Separator className="my-3" />
                    <div className="flex items-center gap-2 text-sm text-emerald-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>Resolved by {item.resolvedByName}</span>
                      {item.resolvedAt && (
                        <span className="text-xs text-muted-foreground">
                          • {format(new Date(item.resolvedAt), 'MMM d, h:mm a')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
